import React, { useState, useEffect, useCallback } from 'react';
import { Box, useMediaQuery, Theme, IconButton, Drawer } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Header from './Header';
import SideNav from './SideNav';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import PageSkeleton from './PageSkeleton';
import HeaderSkeleton from './HeaderSkeleton';
import { EnrollmentStatus, UserStatus } from '../../constant/constant';
import { useUserStatus } from '../../../app/routes/useUserStatus';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [renderContent, setRenderContent] = useState<boolean>(false);
  const [showSidebar, setShowSidebar] = useState<boolean | null>(null);
  const DRAWER_WIDTH = 256;
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isSubscriptionPageActive =
    location.pathname.includes('subscriptions') || location.pathname.includes('checkout');
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const { status } = useUserStatus();
  const hideSidebar = [
    UserStatus.EXITED,
    UserStatus.EXITED_BY_REQUEST,
    UserStatus.EXITED_BY_STOP,
    UserStatus.EXITED_BY_ADMIN,
    UserStatus.IDLE_COACHING,
    UserStatus.COMMUNITY_MEMBER,
    UserStatus.SIGNED_UP,
    UserStatus.PENDING_MATCHING,
    UserStatus.WAITLIST_REVIEW,
  ];

  const _showSidebar = [EnrollmentStatus.FULLY_ENROLLED_ACTIVE, EnrollmentStatus.MEMBERSHIP_ACTIVE];
  const isSideBarShow = _showSidebar.includes(status as EnrollmentStatus);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (basicInfo && basicInfo.currentStatus) {
      const shouldShow = !(
        basicInfo.isCommunityMemberDialog ||
        isSubscriptionPageActive ||
        !isSideBarShow
      );
      setShowSidebar(shouldShow);
      setRenderContent(true);
    }
  }, [basicInfo, basicInfo.currentStatus, isSubscriptionPageActive, isSideBarShow]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  if (!renderContent || showSidebar === null) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Box
          sx={{
            position: 'fixed',
            width: '100%',
            zIndex: 1200,
            backgroundColor: '#FFFFFF',
          }}
        >
          <HeaderSkeleton />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%',
            marginTop: '64px',
          }}
        >
          <Box
            component='main'
            sx={{
              flexGrow: 1,
              p: { xs: 1, sm: 2, md: 3 },
              minHeight: 'calc(100vh - 64px)',
              backgroundColor: '#F9FBFF',
            }}
          >
            <PageSkeleton />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        sx={{
          position: 'fixed',
          width: '100%',
          zIndex: 1200,
          backgroundColor: '#FFFFFF',
        }}
      >
        <Header showLogo={true}>
          {showSidebar && isMobile && (
            <IconButton
              color='inherit'
              aria-label='open drawer'
              edge='start'
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Header>
      </Box>

      {showSidebar && isMobile && (
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF',
            },
          }}
        >
          <SideNav width={DRAWER_WIDTH} onClose={handleDrawerToggle} />
        </Drawer>
      )}

      {showSidebar && !isMobile && (
        <Box
          component='nav'
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            display: { xs: 'none', md: 'block' },
            position: 'fixed',
            height: '100vh',
            zIndex: 1100,
          }}
        >
          <Drawer
            variant='permanent'
            sx={{
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                borderRight: 'none',
                boxShadow: 'none',
                backgroundColor: '#FFFFFF',
                height: '100%',
              },
            }}
            open
          >
            <SideNav width={DRAWER_WIDTH} />
          </Drawer>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          width: showSidebar ? { md: `calc(100% - ${DRAWER_WIDTH}px)` } : '100%',
          marginLeft: showSidebar ? { md: `${DRAWER_WIDTH}px` } : 0,
          marginTop: '64px',
        }}
      >
        <Box
          component='main'
          sx={{
            flexGrow: 1,
            p: { xs: 1, sm: 2, md: 3 },
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
